import type { ToolListGroup } from "../core/tool-families";
import type { AgentTool } from "@/schemas/agent.schema";
import { computed, defineComponent } from "vue";
import Badge from "@/components/ui/badge/Badge";
import Icon from "@/components/ui/icon/Icon";
import Tooltip from "@/components/ui/tooltip/Tooltip";
import { $t } from "@/services/i18n";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import { CALL_ROW_TITLE_KEYS, ESCALATE_ROW_TITLE_KEY, groupToolsForList } from "../core/tool-families";
import { isNeverDeferredTool } from "../core/tool-search";
import { getFamilyIcon, getToolIcon, getToolName } from "../core/utils";
import ToolOriginBadge from "./ToolOriginBadge";

function renderCallRows(group: ToolListGroup) {
  const names = new Set(group.tools.map(tool => tool.tool.name));
  const rows: Array<{ key: string; icon: string; label: string }> = [];
  if (names.has("end_dialog")) {
    rows.push({ key: "end_dialog", icon: "lucide:phone-off", label: $t(CALL_ROW_TITLE_KEYS.end_dialog) });
  }
  if (names.has("transfer_call")) {
    rows.push({ key: "transfer_call", icon: "lucide:phone-forwarded", label: $t(CALL_ROW_TITLE_KEYS.transfer_call) });
  }
  if (names.has("send_dtmf") && names.has("receive_dtmf")) {
    rows.push({ key: "dtmf", icon: "lucide:grid-3x3", label: $t("agents:tool_families.groups.call_dtmf") });
  } else {
    if (names.has("send_dtmf")) {
      rows.push({ key: "send_dtmf", icon: "lucide:phone-outgoing", label: $t(CALL_ROW_TITLE_KEYS.send_dtmf) });
    }
    if (names.has("receive_dtmf")) {
      rows.push({ key: "receive_dtmf", icon: "lucide:grid-3x3", label: $t(CALL_ROW_TITLE_KEYS.receive_dtmf) });
    }
  }
  return rows.map(row => (
    <li key={row.key} class="flex items-center gap-2 text-xs font-normal leading-none">
      <Icon icon={row.icon} class="h-4 w-4 shrink-0 opacity-70" />
      <span class="flex-1 truncate">{row.label}</span>
    </li>
  ));
}

/**
 * AI-1195: tool lists grouped by the same families as the chat, reusing the
 * classifier: "Do agente", one group per MCP server (badge with name + host),
 * "Nativas", "Chamada" and "Atendimento humano". The sempre / sob demanda
 * badges stay on agent, MCP and native rows when in deferred mode.
 */
export default defineComponent({
  name: "ToolGroupsList",
  props: {
    tools: p.$array<AgentTool>(),
    deferredMode: p.boolean(false),
  },
  setup(props) {
    const groups = computed(() => groupToolsForList(props.tools));

    function availabilityBadge(tool: AgentTool) {
      if (!props.deferredMode) return null;
      const isAlways = isNeverDeferredTool(tool);
      return (
        <Tooltip text={isAlways ? $t("agents:tool_search.always_tooltip") : $t("agents:tool_search.on_demand_tooltip")}>
          <Badge size="xs" color={isAlways ? "green" : "gray"} class="shrink-0 cursor-help">
            {isAlways ? $t("agents:tool_search.always") : $t("agents:tool_search.on_demand")}
          </Badge>
        </Tooltip>
      );
    }

    function agentTag(tool: AgentTool) {
      if (tool.type === "api") return <span class="shrink-0 text-2xs text-muted-foreground">API</span>;
      if (tool.type === "rag" || tool.type === "knowledge") {
        return <span class="shrink-0 text-2xs text-muted-foreground">{$t("agents:tool_families.groups.tag_knowledge")}</span>;
      }
      if (tool.type === "function_call") {
        return <span class="shrink-0 text-2xs text-muted-foreground">{$t("agents:tool_families.manual")}</span>;
      }
      return null;
    }

    function renderAgentRows(group: ToolListGroup) {
      return group.tools.map(tool => (
        <li class="flex items-center gap-2 text-xs font-normal leading-none">
          <Icon icon={getToolIcon(tool)} class="h-4 w-4 shrink-0 opacity-70" />
          <span class="flex-1 truncate">{getToolName(tool)}</span>
          {agentTag(tool)}
          {availabilityBadge(tool)}
        </li>
      ));
    }

    /** MCP and native rows: icon, name and the on-demand badge. */
    function renderPlainRows(group: ToolListGroup) {
      return group.tools.map(tool => (
        <li class="flex items-center gap-2 text-xs font-normal leading-none">
          <Icon icon={getToolIcon(tool)} class="h-4 w-4 shrink-0 opacity-70" />
          <span class="flex-1 truncate">{getToolName(tool)}</span>
          {availabilityBadge(tool)}
        </li>
      ));
    }

    return () => (
      <div class="flex flex-col gap-4">
        {groups.value.map((group) => {
          if (group.key === "agent") {
            return (
              <div class="flex flex-col gap-2">
                <span class="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {$t("agents:tool_families.groups.agent")}
                </span>
                <ul class="flex flex-col gap-2">{renderAgentRows(group)}</ul>
              </div>
            );
          }
          if (group.key === "mcp") {
            return (
              <div class="flex flex-col gap-2">
                <span>
                  <ToolOriginBadge
                    family={{
                      kind: "agent_tool",
                      agentType: "mcp",
                      mcp: { serverKey: group.serverKey ?? "", serverName: group.serverName ?? "", serverHost: group.serverHost },
                    }}
                  />
                </span>
                <ul class="flex flex-col gap-2">{renderPlainRows(group)}</ul>
              </div>
            );
          }
          if (group.key === "native") {
            return (
              <div class="flex flex-col gap-2">
                <span class="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {$t("agents:tool_families.groups.native")}
                </span>
                <ul class="flex flex-col gap-2">{renderPlainRows(group)}</ul>
              </div>
            );
          }
          if (group.key === "call_control") {
            return (
              <div class="flex flex-col gap-2">
                <span class={cn("text-2xs font-semibold uppercase tracking-wider text-violet-11")}>
                  {$t("agents:tool_families.groups.call")}
                </span>
                <ul class="flex flex-col gap-2">{renderCallRows(group)}</ul>
              </div>
            );
          }
          return (
            <div class="flex flex-col gap-2">
              <span class={cn("text-2xs font-semibold uppercase tracking-wider text-teal-11")}>
                {$t("agents:tool_families.groups.escalation")}
              </span>
              <ul class="flex flex-col gap-2">
                <li class="flex items-center gap-2 text-xs font-normal leading-none">
                  <Icon icon={getFamilyIcon({ kind: "escalation" })} class="h-4 w-4 shrink-0 opacity-70" />
                  <span class="flex-1 truncate">{$t(ESCALATE_ROW_TITLE_KEY)}</span>
                </li>
              </ul>
            </div>
          );
        })}
      </div>
    );
  },
});
